import { useState } from "react";
import {
  FIRM_RULE_LABELS,
  HELM_CALCULATED_LABEL,
  INCOMPLETE_HISTORY,
  OWNER_ENTRY_LABEL,
  firmRule,
  unavailableText,
  useSettings,
  type BrokerFigure,
  type FigureInput,
  type FigureKind,
  type FirmRuleKind,
  type FirmRules,
} from "@helm/core";
import { formatCents } from "@helm/ui";
import type { AccountRow } from "../viewmodel/account";
import { saveVerification, useVerification } from "./brokerVerification";

/**
 * One account's risk figures: drawdown room and daily-loss room from Rithmic values (ADR-0045),
 * or — where Rithmic has none — HELM-calculated from the owner's firm rules (ADR-0047), each with
 * its source ("Rithmic · unverified" / "Rithmic · verified" / "HELM-calculated from your firm
 * rules" / "Incomplete history" / "Unavailable"), formula, every input and any "differs from
 * Rithmic" note; Rithmic's threshold, peak, loss limit and criteria; and the firm rules ("Firm rule
 * · your entry") with the figures HELM calculates from them. Shared by the desktop disclosure,
 * desktop/mobile account settings and mobile Home.
 */
export function BrokerRiskFigures({ row }: { row: AccountRow }): React.JSX.Element {
  const privacy = useSettings((s) => s.settings.privacy);
  const money = (v: bigint | null): string => formatCents(v, { privacy });
  const b = row.broker;
  const r = b.inputs.risk;
  return (
    <div className="broker-risk" data-broker-risk={privacy ? undefined : row.summary.id}>
      <h4>Risk room</h4>
      <FigureBlock accountId={row.summary.id} title="Drawdown room" figure={b.trailingDrawdown} row={row} />
      <FigureBlock accountId={row.summary.id} title="Daily loss room" figure={b.dailyLoss} row={row} />
      <dl>
        <dt>Auto-liq threshold</dt>
        <dd title={b.threshold.reason || "Rithmic 356 auto_liq_threshold_current_value, reported directly"}>
          {b.threshold.valueCents === null ? "Unavailable" : money(b.threshold.valueCents)}
          {b.threshold.valueCents !== null && <span className="source-tag source-direct">Rithmic</span>}
        </dd>
        <dt>Peak balance</dt>
        <dd title="Rithmic 356 auto_liq_peak_account_balance, reported directly">
          {b.peakCents === null ? "Unavailable" : money(b.peakCents)}
          {b.peakCents !== null && <span className="source-tag source-direct">Rithmic</span>}
        </dd>
        <dt>Auto-liquidate criteria</dt>
        <dd>{r?.rmsInfoCurrent ? r.autoLiquidateCriteria || "Not reported" : "Unavailable"}</dd>
      </dl>
      {b.threshold.reason !== "" && <p className="muted">Threshold: {b.threshold.reason}.</p>}
      <h4>Firm rules</h4>
      <dl>
        {(Object.keys(FIRM_RULE_LABELS) as FirmRuleKind[]).map((kind) => {
          const f = firmRule(kind, entryOf(kind, b.firmRules, money));
          return <FirmRuleRow key={kind} label={f.label} value={f.value} reason={f.reason} />;
        })}
      </dl>
      <FirmFigures row={row} />
    </div>
  );
}

/** The owner's entry of one firm rule, formatted (undefined: no rules saved at all). */
function entryOf(
  kind: FirmRuleKind,
  r: FirmRules | null,
  money: (v: bigint | null) => string,
): { value: string | null } | undefined {
  if (r === null) return undefined;
  switch (kind) {
    case "profit_target":
      return { value: r.profit_target_cents === null ? null : money(BigInt(r.profit_target_cents)) };
    case "consistency":
      return { value: r.consistency_pct === null ? null : `${String(r.consistency_pct)}%` };
    case "payout_window":
      return {
        value:
          r.payout_window_start === null
            ? null
            : `from ${r.payout_window_start}${r.payout_cycle_days === null ? "" : `, every ${String(r.payout_cycle_days)} days`}`,
      };
  }
}

function FirmRuleRow({
  label,
  value,
  reason,
}: {
  label: string;
  value: string | null;
  reason: string;
}): React.JSX.Element {
  return (
    <>
      <dt>{label}</dt>
      <dd data-firm-rule={label}>
        {value === null ? (
          <>Unavailable · {reason.toLowerCase()}</>
        ) : (
          <>
            {value}
            <span className="source-tag source-owner">{OWNER_ENTRY_LABEL}</span>
          </>
        )}
      </dd>
    </>
  );
}

/** Target room, consistency and payout-period P&L, HELM-calculated from the owner's rules. */
function FirmFigures({ row }: { row: AccountRow }): React.JSX.Element | null {
  const privacy = useSettings((s) => s.settings.privacy);
  const money = (v: bigint | null): string => formatCents(v, { privacy });
  const f = row.broker.firm;
  // No rules saved, or the firm-rules chunk not loaded yet (no rules are known then either).
  if (row.broker.firmRules === null || f === null) return null;
  const c = f.consistency;
  const consistencyText =
    c.status === "measured"
      ? `${String(c.ratioPct)}% of ${String(c.limitPct)}%`
      : c.status === "not_measurable"
        ? "Not yet measurable"
        : c.label === INCOMPLETE_HISTORY
          ? INCOMPLETE_HISTORY
          : "Unavailable";
  const today =
    c.status !== "measured"
      ? null
      : c.unlimited
        ? "No cap today"
        : c.maxTodayCents !== null
          ? `Up to ${money(c.maxTodayCents)} today`
          : null;
  return (
    <div className="firm-figures" data-firm-figures={privacy ? undefined : row.summary.id}>
      <HelmRow
        name="Target room"
        value={f.targetRoom.valueCents === null ? null : money(f.targetRoom.valueCents)}
        extra={f.targetRoom.progressPct === null ? null : `${f.targetRoom.progressPct.toFixed(1)}% of target`}
        label={f.targetRoom.label}
        reason={f.targetRoom.reason}
        formula={f.targetRoom.formula}
        inputs={f.targetRoom.inputs}
        kind="target"
      />
      <HelmRow
        name="Consistency"
        value={c.source === "unavailable" ? null : consistencyText}
        extra={today}
        label={c.label}
        reason={c.reason}
        formula={`${c.formula}${c.window ? ` Window ${c.window} (${String(c.days)} past day(s)).` : ""}`}
        inputs={c.inputs}
        kind="consistency"
      />
      <HelmRow
        name="Payout-period P&L"
        value={f.payoutPnl.valueCents === null ? null : money(f.payoutPnl.valueCents)}
        extra={f.payoutPnl.window ? `Window ${f.payoutPnl.window}` : null}
        label={f.payoutPnl.label}
        reason={f.payoutPnl.reason}
        formula={f.payoutPnl.formula}
        inputs={f.payoutPnl.inputs}
        kind="payout"
      />
    </div>
  );
}

const tagOf = (label: string, hasValue: boolean): string =>
  !hasValue ? "source-unavailable" : label === HELM_CALCULATED_LABEL ? "source-helm" : "source-unverified";

function HelmRow({
  name,
  value,
  extra,
  label,
  reason,
  formula,
  inputs,
  kind,
}: {
  name: string;
  value: string | null;
  extra: string | null;
  label: string;
  reason: string;
  formula: string;
  inputs: FigureInput[];
  kind: string;
}): React.JSX.Element {
  return (
    <div className="broker-figure" data-helm-figure={kind}>
      <div className="broker-figure-head">
        <span>{name}</span>
        <strong className="num">
          {value ?? (label === INCOMPLETE_HISTORY ? INCOMPLETE_HISTORY : "Unavailable")}
        </strong>
        <span className={`source-tag ${tagOf(label, value !== null)}`}>{label}</span>
      </div>
      {extra !== null && <p className="broker-figure-reason">{extra}</p>}
      {reason !== "" && <p className="broker-figure-reason">{reason}</p>}
      <details>
        <summary>Formula and inputs</summary>
        <p>{formula}</p>
        <dl>
          {inputs.map((i) => (
            <InputRow key={i.label} label={i.label} value={i.valueCents} raw={i.raw} reason={i.reason} />
          ))}
        </dl>
      </details>
    </div>
  );
}

function FigureBlock({
  accountId,
  title,
  figure,
  row,
}: {
  accountId: string;
  title: string;
  figure: BrokerFigure;
  row: AccountRow;
}): React.JSX.Element {
  const privacy = useSettings((s) => s.settings.privacy);
  const money = (v: bigint | null): string => formatCents(v, { privacy });
  const tag =
    figure.source === "unavailable"
      ? "source-unavailable"
      : figure.source === "helm_calculated"
        ? "source-helm"
        : figure.verification === "verified"
          ? "source-verified"
          : "source-unverified";
  // The Rithmic figure the owner can verify: this one, or the unavailable Rithmic one beside a
  // HELM-calculated figure.
  const rithmicFigure = figure.source === "helm_calculated" ? (figure.alternate ?? null) : figure;
  const other = figure.alternate ?? null;
  return (
    <div className="broker-figure" data-figure={figure.kind} data-source={figure.source}>
      <div className="broker-figure-head">
        <span>{title}</span>
        <strong className="num">
          {figure.valueCents === null ? "Unavailable" : money(figure.valueCents)}
        </strong>
        <span className={`source-tag ${tag}`}>{figure.label}</span>
      </div>
      {figure.reason !== "" && <p className="broker-figure-reason">{figure.reason}</p>}
      {figure.note && (
        <p className="broker-figure-note" data-figure-note={figure.kind}>
          {figure.note}
        </p>
      )}
      <details>
        <summary>
          {figure.source === "helm_calculated"
            ? "Formula and inputs (TradePrizm-calculated)"
            : "Formula and Rithmic inputs"}
        </summary>
        {figure.formula && (
          <p>
            <strong>{figure.formula.label}</strong>: {figure.formula.expression}
          </p>
        )}
        <dl>
          {figure.inputs.map((i) => (
            <InputRow key={i.label} label={i.label} value={i.valueCents} raw={i.raw} reason={i.reason} />
          ))}
        </dl>
        <p className="muted">
          {figure.source === "helm_calculated"
            ? "Both treatments of open P&L:"
            : "Candidates kept for the R|Trader Pro match:"}
        </p>
        <dl>
          {figure.candidates.map((c) => (
            <CandidateRow
              key={c.formula.id}
              label={`${c.formula.label}${c.formula.id === figure.formula?.id ? " (shown)" : ""}`}
              value={c.valueCents === null ? unavailableText(c.reason) : money(c.valueCents)}
            />
          ))}
        </dl>
        {other !== null && (
          <div className="broker-figure-other" data-other-source={other.source}>
            <p>
              <strong>{figure.source === "helm_calculated" ? "Rithmic" : HELM_CALCULATED_LABEL}</strong>
              {": "}
              {other.valueCents === null ? unavailableText(other.reason) : money(other.valueCents)}
            </p>
            {other.formula && <p className="muted">{other.formula.expression}</p>}
            <dl>
              {other.inputs.map((i) => (
                <InputRow key={i.label} label={i.label} value={i.valueCents} raw={i.raw} reason={i.reason} />
              ))}
            </dl>
          </div>
        )}
        {rithmicFigure !== null && <VerifyControls accountId={accountId} figure={rithmicFigure} row={row} />}
      </details>
    </div>
  );
}

function InputRow({
  label,
  value,
  raw,
  reason,
}: {
  label: string;
  value: bigint | null;
  raw: string | null;
  reason: string;
}): React.JSX.Element {
  const privacy = useSettings((s) => s.settings.privacy);
  const text =
    value !== null
      ? formatCents(value, { privacy })
      : reason !== ""
        ? unavailableText(reason)
        : "Unavailable";
  return (
    <>
      <dt>{label}</dt>
      <dd>
        {text}
        {raw !== null && !privacy && <span className="muted"> (sent as “{raw}”)</span>}
      </dd>
    </>
  );
}

function CandidateRow({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/**
 * The owner's explicit R|Trader Pro match: choose the formula whose value R|Trader showed, type
 * what R|Trader showed, confirm. Withdrawing returns the figure to "unverified". Nothing here runs
 * on its own.
 */
function VerifyControls({
  accountId,
  figure,
  row,
}: {
  accountId: string;
  figure: BrokerFigure;
  row: AccountRow;
}): React.JSX.Element {
  const v = useVerification(accountId);
  const entry = v.doc?.[figure.kind] ?? null;
  const [open, setOpen] = useState(false);
  const [formula, setFormula] = useState(figure.formula?.id ?? "");
  const [rtrader, setRtrader] = useState("");
  const [note, setNote] = useState("");
  const [compared, setCompared] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const busy = v.status === "saving";
  const chosen = figure.candidates.find((c) => c.formula.id === formula) ?? null;
  const canConfirm =
    compared && rtrader.trim() !== "" && chosen !== null && chosen.valueCents !== null && !busy;
  const kindName: Record<FigureKind, string> = {
    trailing_drawdown: "drawdown room",
    daily_loss: "daily loss room",
  };
  return (
    <div className="broker-verify">
      {entry ? (
        <p>
          Matched against R|Trader Pro on {new Date(entry.confirmed_at).toLocaleString()} from{" "}
          {entry.device_label || "a device"} (
          {entry.scope === "flat" ? "while flat" : "with an open position"}; R|Trader showed “
          {entry.rtrader_value}”).
        </p>
      ) : (
        <p className="muted">No R|Trader Pro match recorded for this {kindName[figure.kind]}.</p>
      )}
      {result && <p role="status">{result}</p>}
      {!open && (
        <button
          type="button"
          className="btn btn-sm"
          disabled={figure.source === "unavailable" || busy}
          onClick={() => {
            setOpen(true);
            setResult(null);
          }}
        >
          Record R|Trader Pro match…
        </button>
      )}
      {entry && !open && (
        <button
          type="button"
          className="btn btn-sm"
          disabled={busy}
          onClick={() => {
            void saveVerification(accountId, figure.kind, null).then((out) => {
              setResult(out.message);
            });
          }}
        >
          Withdraw verification
        </button>
      )}
      {open && (
        <form
          className="broker-verify-form"
          aria-label={`Record R|Trader Pro match: ${kindName[figure.kind]}`}
          onSubmit={(e) => {
            e.preventDefault();
            if (!canConfirm) return;
            void saveVerification(accountId, figure.kind, {
              formula,
              scope: row.broker.inputs.unrealizedCents === 0n ? "flat" : "open_position",
              criteria: row.broker.inputs.risk?.autoLiquidateCriteria ?? "",
              helm_value_cents: String(chosen.valueCents),
              rtrader_value: rtrader.trim(),
              note: note.trim(),
            }).then((out) => {
              setResult(out.message);
              if (out.ok) setOpen(false);
            });
          }}
        >
          <label>
            Formula whose value R|Trader Pro showed
            <select
              className="input"
              value={formula}
              onChange={(e) => {
                setFormula(e.target.value);
              }}
            >
              {figure.candidates.map((c) => (
                <option key={c.formula.id} value={c.formula.id} disabled={c.valueCents === null}>
                  {c.formula.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Value R|Trader Pro showed
            <input
              className="input"
              value={rtrader}
              onChange={(e) => {
                setRtrader(e.target.value);
              }}
            />
          </label>
          <label>
            Note (optional)
            <input
              className="input"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={compared}
              onChange={(e) => {
                setCompared(e.target.checked);
              }}
            />
            <span>
              I compared this account in R|Trader Pro and its value equals TradePrizm’s{" "}
              {chosen?.valueCents === null || chosen === null ? "figure" : formatCents(chosen.valueCents)}.
            </span>
          </label>
          <div className="broker-verify-actions">
            <button type="submit" className="btn btn-sm" disabled={!canConfirm}>
              Confirm match
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
